import React, { Suspense } from 'react';

/* eslint-disable import/order */
import 'core-js';
/* eslint-enable import/order */

import * as Sentry from '@sentry/react';
import { parse } from 'query-string';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import packageInfo from '../package.json';

// NOTE: These need to be kept above loading our components.
/* eslint-disable import/order */
import { App } from './App';
import { worker } from './__mocks__';
import './i18n';
import './index.scss';
/* eslint-enable import/order */

import { RedirectSingleton, RedirectType } from './classes';
import YatLoader from './components/Sections/Profile/Loader';
import config from './config';
import { get_anonymous_id, onLoadUrlParams, waitUntilAnalyticsIsReady } from './helpers';
import { QueryClientProvider, reactQueryClient } from './reactQueryClient';
import { type DeferredStore, initReduxStore } from './store';
import {
    AttributionEnum,
    type AttributionProperties,
    setAttributionProperties,
} from './store/types/attribution_properties';
import { type UserProperties } from './store/types/user_properties';
import { validateRedirect } from './utilities';

const { publicUrl } = config;

Sentry.init({
    dsn: 'https://ac07138713ef4e9bab59adb95b927472@o4504839079002112.ingest.us.sentry.io/4505091043033088',
    enabled: config.envType !== 'local',
    environment: config.envType,
    release: packageInfo.version,
    integrations: [
        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: [
    //     'localhost',
    //     /^https:\/\/yat\.fyi\/a/,
    //     /^https:\/\/yat\.rip\/a/,
    //     /^https:\/\/y\.at\/a/,
    //     /^https:\/\/yat\.fyi\/nbe/,
    //     /^https:\/\/yat\.rip\/nbe/,
    //     /^https:\/\/y\.at\/nbe/,
    //     /^https:\/\/micro\.yat\.fyi\/a/,
    //     /^https:\/\/micro\.yat\.rip\/a/,
    //     /^https:\/\/micro\.y\.at\/a/,
    //     /^https:\/\/micro\.yat\.fyi\/a/,
    //     /^https:\/\/micro\.yat\.rip\/a/,
    //     /^https:\/\/micro\.y\.at\/a/,
    // ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

const linkPrefix = `${window.location.protocol}//`;
const baseUrl = `${linkPrefix}${publicUrl}`;
window.twoFactorCallbackQueue = [];

let initializedStore: DeferredStore | null = null;

const container = document.getElementById('root');

//eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

function render(AppComponent: () => React.ReactElement) {
    // Should `Suspense` not be the top-most provider?
    // That way, we can render the `fallback` while waiting
    // for `initializedStore` to be defined?
    root.render(
        <React.StrictMode>
            {initializedStore ? (
                <ReduxProvider store={initializedStore}>
                    <QueryClientProvider client={reactQueryClient}>
                        <Suspense fallback={<YatLoader />}>
                            <AppComponent />
                        </Suspense>
                    </QueryClientProvider>
                </ReduxProvider>
            ) : null}
        </React.StrictMode>,
    );
}

function load() {
    return <React.Fragment />;
}

function checkStartPageRedirect() {
    const storedRedirect = RedirectSingleton.getRedirect(RedirectType.Default);
    let waitlistRedirect: URL | null = null;

    try {
        waitlistRedirect = storedRedirect ? new URL(storedRedirect) : null;
    } catch (e) {
        // Added try, in case it's just '/checkout' and the URL isn't valid,
        // so it doesn't throw an error when checking .origin
    }

    const waitListUrl = config.redirectRoot ? new URL(config.redirectRoot) : undefined;

    if (waitlistRedirect && waitlistRedirect.origin === waitListUrl?.origin) {
        RedirectSingleton.removeRedirect(RedirectType.Default);
        window.location.href = waitlistRedirect.href;
    }
}

waitUntilAnalyticsIsReady(async () => {
    // please don't remove this from the top of this function
    // we need to load the tokens before initReduxStore()
    await onLoadUrlParams((tokens, redirectUrl) => {
        // ~~ bitwise NOT and used as a faster floor.
        // http://rocha.la/JavaScript-bitwise-operators-in-practice
        const currentUnixTimestamp = ~~(+new Date() / 1000);
        const tokenExpired = tokens.refresh_token && tokens.refresh_token.exp <= currentUnixTimestamp;

        // Set the expired link if the token has expired.
        RedirectSingleton.setRedirect(RedirectType.Expired, tokenExpired ? '/link-expired' : false);

        // Set the custom link if the `token` has not expired,
        // and there is a valid `redirectUrl`.
        RedirectSingleton.setRedirect(
            RedirectType.Default,
            !tokenExpired && redirectUrl && validateRedirect(redirectUrl, baseUrl) ? redirectUrl : false,
        );
    });

    const query = parse(window.location.search);
    const attributionProperties: AttributionProperties = {};

    Object.keys(AttributionEnum).forEach((key) => {
        if (query[key]) {
            attributionProperties[key] = query[key];
        }
    });

    // Before redirecting the user we need to collect the UTM parameters
    setAttributionProperties(attributionProperties);

    // Should stay below onLoadUrlParams()
    render(load);

    initReduxStore().then((result) => {
        initializedStore = result;
        checkStartPageRedirect();
        render(App);
    });

    const amplitude = window.amplitude && window.amplitude.getInstance();

    if (amplitude) {
        const deviceId = amplitude.options.deviceId;
        if (deviceId) {
            window.localStorage.setItem('amplitudeDeviceId', deviceId);
        }

        const sessionId = amplitude.getSessionId();
        if (sessionId) {
            window.localStorage.setItem('amplitudeSessionId', sessionId);
        }

        const referrer = document.referrer;
        const userProperties: UserProperties = {};
        Object.keys(AttributionEnum).forEach((key) => {
            if (attributionProperties[key]) {
                userProperties[key] = attributionProperties[key];
            }
        });

        if (referrer !== '') {
            const domain_anchor = document.createElement('a');
            domain_anchor.href = referrer;
            userProperties.referrer = referrer;
            userProperties.referring_domain = domain_anchor.hostname;
        }
        amplitude.setUserProperties(userProperties);
        amplitude.setVersionName(packageInfo.version);
    }

    const anonymous_id = get_anonymous_id();

    if (anonymous_id) {
        window.analytics.setAnonymousId(anonymous_id);
    }

    if (config.fbPixelId) {
        window.fbq('init', config.fbPixelId);
        window.fbq('track', 'PageView');
    }
    if (config.redditPixelId) {
        window.rdt('init', config.redditPixelId);
        window.rdt('track', 'PageVisit');
    }
});

if (process.env.NODE_ENV === 'development') {
    if (config.useMockServer) {
        worker.start({
            onUnhandledRequest: 'bypass',
        });
    }

    if (module.hot) {
        module.hot.accept('./App', () => {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const NextApp = App;
            render(NextApp);
        });
    }
}
